import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Heart } from "lucide-react";
import GroveLogo from "./GroveLogo";

/* Tiny rotating sparkle used between payment-method labels in the footer */
const SpinDot = () => (
  <span
    aria-hidden="true"
    className="inline-flex items-center justify-center"
    style={{ width: 12, height: 12, animation: "ggSpinFoot 3.6s linear infinite" }}
  >
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none">
      <path
        d="M12 2 L13.4 9.4 L21.5 11.2 L13.4 13 L12 22 L10.6 13 L2.5 11.2 L10.6 9.4 Z"
        fill="#4A2C35"
      />
    </svg>
  </span>
);

const Footer = () => {
  return (
    <footer
      className="mt-24 bg-[#FBEAF0]/60 border-t border-sectionbreak"
      data-testid="site-footer"
    >
      <style>{`
        @keyframes ggSpinFoot {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-14">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <GroveLogo height={64} />
            </Link>
            <p className="text-sm text-plum/70 leading-relaxed mb-5 max-w-[260px]">
              Adorned in your own story. Premium imitation jewelry, curated for the modern Indian woman.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/girlygroveofficial/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-plum hover:bg-gold hover:text-cream hover:border-gold transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram size={15} />
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-plum hover:bg-gold hover:text-cream hover:border-gold transition-colors"
                aria-label="Pinterest"
                data-testid="footer-pinterest"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0a12 12 0 00-4.4 23.16c-.06-.94-.12-2.4.04-3.43.13-.92 1.34-5.87 1.34-5.87s-.34-.69-.34-1.7c0-1.6.93-2.79 2.09-2.79.98 0 1.46.74 1.46 1.63 0 .99-.63 2.47-.95 3.84-.27 1.15.58 2.09 1.71 2.09 2.05 0 3.63-2.17 3.63-5.3 0-2.77-1.99-4.7-4.83-4.7-3.29 0-5.22 2.47-5.22 5.02 0 .99.38 2.06.86 2.64.09.12.11.22.08.34l-.32 1.31c-.05.21-.17.26-.39.16-1.45-.68-2.36-2.79-2.36-4.5 0-3.66 2.66-7.02 7.66-7.02 4.02 0 7.15 2.87 7.15 6.7 0 4-2.52 7.22-6.02 7.22-1.18 0-2.28-.61-2.66-1.34l-.72 2.76c-.26 1-.97 2.26-1.45 3.03A12 12 0 1012 0z"/></svg>
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-plum hover:bg-gold hover:text-cream hover:border-gold transition-colors"
                aria-label="WhatsApp"
                data-testid="footer-whatsapp"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.687-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.888-9.882.002-5.459-4.415-9.884-9.881-9.886-5.452 0-9.887 4.434-9.889 9.881-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.743-.987zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-plum hover:bg-gold hover:text-cream hover:border-gold transition-colors"
                aria-label="YouTube"
                data-testid="footer-youtube"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.017 3.017 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.546 15.568V8.432L15.818 12l-6.272 3.568z"/></svg>
              </a>
            </div>
          </div>

          <div>
            <h4 className="overline mb-5">Shop</h4>
            <ul className="space-y-3 text-sm text-plum/80">
              <li><Link to="/shop" className="hover:text-blush" data-testid="footer-new-in">New In</Link></li>
              <li><Link to="/shop?category=earrings" className="hover:text-blush">Earrings</Link></li>
              <li><Link to="/shop?category=necklaces" className="hover:text-blush">Necklaces</Link></li>
              <li><Link to="/shop?category=rings" className="hover:text-blush">Rings</Link></li>
              <li><Link to="/shop?category=bracelets" className="hover:text-blush">Bracelets</Link></li>
              <li><Link to="/shop?category=sets" className="hover:text-blush">Sets</Link></li>
              <li><Link to="/shop?category=gifts" className="hover:text-blush">Gifts</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="overline mb-5">Help</h4>
            <ul className="space-y-3 text-sm text-plum/80">
              <li><a href="mailto:support@girlygrove.in" className="hover:text-blush">Contact Us</a></li>
              <li><Link to="/policy/shipping" className="hover:text-blush">Shipping Policy</Link></li>
              <li><Link to="/policy/refund" className="hover:text-blush">Refund &amp; Returns</Link></li>
              <li><Link to="/policy/privacy" className="hover:text-blush">Privacy Policy</Link></li>
              <li><Link to="/policy/terms" className="hover:text-blush">Terms of Service</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="overline mb-5">About</h4>
            <ul className="space-y-3 text-sm text-plum/80">
              <li><a href="#" className="hover:text-blush">Our Story</a></li>
              <li><a href="#" className="hover:text-blush">Blog</a></li>
              <li><a href="#" className="hover:text-blush">Influencer Program</a></li>
              <li><a href="#" className="hover:text-blush">Affiliate</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 flex flex-col md:flex-row items-center justify-between gap-5">
          <p className="text-xs text-plum/60 tracking-wide flex items-center gap-1.5">
            © 2026 GirlyGrove. All rights reserved · Made with
            <Heart size={11} className="text-blush fill-blush" />
            in India
          </p>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.14em] text-plum/60 uppercase">
            <span>UPI</span>
            <SpinDot />
            <span>Razorpay</span>
            <SpinDot />
            <span>COD</span>
            <SpinDot />
            <span>Visa</span>
            <SpinDot />
            <span>Mastercard</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
